import { useQuery } from '@tanstack/react-query'
import { carePlanService } from '../services/carePlanService'
import { formatDate } from '../utils/locale'
import './CarePlanHistory.css'

interface CarePlanHistoryProps {
  carePlanId: string
}

export function CarePlanHistory({ carePlanId }: CarePlanHistoryProps) {
  const { data: history, isLoading, error } = useQuery({
    queryKey: ['care-plan-history', carePlanId],
    queryFn: () => carePlanService.getHistory(carePlanId),
    enabled: !!carePlanId,
  })

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'create':
        return 'var(--color-success)'
      case 'update':
        return 'var(--color-info)'
      case 'delete':
        return 'var(--color-danger)'
      default:
        return 'var(--color-text)'
    }
  }

  const formatChangeValue = (value: any): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  if (isLoading) {
    return <div className="care-plan-history-loading">Loading history...</div>
  }

  if (error) {
    return (
      <div className="care-plan-history-error">
        Error loading history: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    )
  }

  // Ensure history is always an array
  const entries = Array.isArray(history) ? history : []

  if (entries.length === 0) {
    return (
      <div className="care-plan-history-empty">
        <p>No history recorded for this care plan yet.</p>
      </div>
    )
  }

  return (
    <div className="care-plan-history">
      <div className="care-plan-history-list">
        {entries.map((entry: any) => (
          <div key={entry.id} className="care-plan-history-entry">
            <div className="care-plan-history-entry-header">
              <div className="care-plan-history-entry-meta">
                <span
                  className="care-plan-history-change-type"
                  style={{ color: getChangeTypeColor(entry.changeType || 'unknown') }}
                >
                  {(entry.changeType || 'unknown').toUpperCase()}
                </span>
                <span className="care-plan-history-timestamp">
                  {formatDate(entry.timestamp, 'dd/MM/yyyy HH:mm')}
                </span>
                <span className="care-plan-history-user">
                  {entry.performedByDisplayName || entry.performedBy}
                </span>
                {entry.reason && (
                  <span className="care-plan-history-reason">{entry.reason}</span>
                )}
              </div>
            </div>

            {entry.changes && entry.changes.length > 0 && (
              <div className="care-plan-history-changes">
                <details>
                  <summary>View Changes ({entry.changes.length})</summary>
                  <div className="care-plan-history-changes-list">
                    {entry.changes.map((change: any, idx: number) => (
                      <div key={idx} className="care-plan-history-change">
                        <div className="care-plan-history-change-field">
                          <strong>Field:</strong> {change.field}
                        </div>
                        <div className="care-plan-history-change-values">
                          <div className="care-plan-history-change-value">
                            <strong>Previous:</strong>
                            <pre>{formatChangeValue(change.previousValue)}</pre>
                          </div>
                          <div className="care-plan-history-change-value">
                            <strong>New:</strong>
                            <pre>{formatChangeValue(change.newValue)}</pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
